import {
    ConfigurationField,
    ConfigurationProvider,
    ConfigurationProviderSettings,
    ConfigurationValue,
} from "./types"

export class Configuration<
    // the types for the configuration - used to display
    CV = object,
    // the options for providers
    PN extends string = never,
    P extends ConfigurationProviderSettings<PN> = never,
> {
    #providers: Record<string, ConfigurationProvider<PN, P>> = {}
    #fields = {} as Record<
        keyof CV,
        ConfigurationField<string, "string" | "number" | "boolean", boolean, PN, P> & {
            marshal: (v: ConfigurationValue | null) => ConfigurationValue | null
        }
    >
    #onChange: Array<() => void> = []

    /**
     * add a provider to the configuration
     * providers are places you can get configuration from
     * @param provider the logic for getting configuration values
     * @returns configuration
     */
    addProvider<N extends string, C extends ConfigurationProviderSettings<N>>(
        provider: ConfigurationProvider<N, C>
    ): Configuration<CV, PN | N, P | C> {
        const next = this as Configuration<CV, PN | N, P | C>
        next.#providers[provider.name] = provider as ConfigurationProvider<PN | N, P | C>
        if (provider.subscribe) {
            provider.subscribe(() => {
                for (const p of this.#onChange) {
                    p()
                }
            })
        }
        return next
    }

    /**
     * Add a field to the configuration
     * Why so many overloads, you ask?
     * Well, we need an overload for each cell in the matrix of configuration types & required or not. This gives us the correct typing on the get & subscribe side
     * @param field the definition for the field
     */
    addField<N extends string>(
        field: ConfigurationField<N, "string", true, PN, P>
    ): Configuration<CV & Record<N, string>, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "number", true, PN, P>
    ): Configuration<CV & Record<N, number>, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "boolean", true, PN, P>
    ): Configuration<CV & Record<N, boolean>, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "string", false, PN, P>
    ): Configuration<CV & Record<N, string | null>, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "number", false, PN, P>
    ): Configuration<CV & Record<N, number | null>, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "boolean", false, PN, P>
    ): Configuration<CV & Record<N, boolean | null>, PN, P>
    addField(
        field: ConfigurationField<string, "string" | "number" | "boolean", boolean, PN, P>
    ): Configuration<CV, PN, P> {
        this.#fields[field.name as keyof CV] = {
            ...field,
            marshal: (v) => {
                if (v === null) {
                    if (field.required) {
                        throw new Error("@nova/configuration -- required field is missing a value")
                    } else {
                        return null
                    }
                }
                switch (field.type) {
                    case "string":
                        if (typeof v !== "string") {
                            return v.toString()
                        } else {
                            return v
                        }
                    case "boolean":
                        return !!v
                    case "number":
                        if (typeof v !== "number") {
                            return NaN
                        } else {
                            return v
                        }
                    default:
                        throw new Error("@nova/configuration -- unrecognized type")
                }
            },
        }
        return this
    }

    /**
     * this gets the current value of a field
     * @param name of the field
     * @returns the current value for this configuration value
     */
    get<K extends keyof CV>(name: K): CV[K] {
        const field = this.#fields[name]
        if (!field) {
            throw new Error(
                `@nova/configuration -- configuration rollup is missing a field ${JSON.stringify(
                    name
                )}`
            )
        }

        for (let i = 0; i < field.from.length; i++) {
            const source = field.from[i]
            const provider = this.#providers[source.provider]
            if (!provider) {
                throw new Error(
                    `@nova/configuration -- configuration rollup is missing a provider ${JSON.stringify(
                        name
                    )}`
                )
            }
            const value = provider.get(name as string, source)
            if (value !== undefined) {
                return field.marshal(value) as CV[K]
            }
            // move onto next highest priority
        }
        if (field.default !== undefined && field.default !== null) {
            return field.default as CV[K]
        }
        if (field?.required === false) {
            return null as CV[K]
        }
        throw new Error(
            `@nova/configuration -- no provider able to get a value for field ${JSON.stringify(
                name
            )}`
        )
    }

    /**
     * subscribes to a specific field
     * @param name of the field
     * @param callback that is called when the field has changed
     * @returns destructor callback - calling this function will stop getting new callbacks
     */
    subscribe<K extends keyof CV>(name: K, callback: (v: CV[K]) => void): () => void {
        let lastValue = this.get(name)
        const onChange = () => {
            const nextValue = this.get(name)
            if (lastValue != nextValue) {
                lastValue = nextValue
                callback(this.get(name) as CV[K])
            }
        }

        this.#onChange.push(onChange)
        return () => {
            this.#onChange = this.#onChange.filter((h) => h !== onChange)
        }
    }

    /**
     * checks the configuration by trying to get every known field. this is useful to see if there is a misconfiguration somewhere
     * @returns the configuration
     */
    check(): { knownKeys: string[]; requiredKeys: string[]; optionalKeys: string[] } {
        const knownKeys: string[] = []
        const requiredKeys: string[] = []
        const optionalKeys: string[] = []
        for (const f in this.#fields) {
            knownKeys.push(f)
            try {
                this.get(f)
            } catch {
                if (this.isRequired(f)) {
                    requiredKeys.push(f)
                } else {
                    optionalKeys.push(f)
                }
            }
        }
        return {
            knownKeys,
            requiredKeys,
            optionalKeys,
        }
    }
    isRequired<K extends keyof CV>(name: K): boolean {
        const field = this.#fields[name]
        if (!field) {
            throw new Error(
                `@nova/configuration -- configuration rollup is missing a field ${JSON.stringify(
                    name
                )}`
            )
        }
        return field.required
    }
}
